import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { PermissionKey } from '@/types/permission';
import { TenantModuleKey } from '@/types/tenant';
import { Skeleton } from '@/components/ui/skeleton';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredPermission?: PermissionKey;
  requiredPermissions?: PermissionKey[];
  requireAnyPermission?: PermissionKey[];
  superAdminOnly?: boolean;
  tenantAdminOnly?: boolean;
  requiredModule?: TenantModuleKey;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-2xl font-bold text-primary-foreground">
          R
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    </div>
  );
}

export function AuthGuard({
  children,
  requireAuth = true,
  requiredPermission,
  requiredPermissions,
  requireAnyPermission,
  superAdminOnly = false,
  tenantAdminOnly = false,
  requiredModule,
}: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { hasPermission, hasAllPermissions, hasAnyPermission, isSuperAdmin, isTenantAdmin, hasModule } =
    useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Check authentication - save current path and redirect to login
  if (requireAuth && !isAuthenticated) {
    sessionStorage.setItem('login-return-to', location.pathname + location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If not requiring auth but user is authenticated, let them through
  // The login page itself can decide what to do (e.g. show "already logged in" or redirect)

  // Check super admin requirement
  if (superAdminOnly && !isSuperAdmin()) {
    return <Navigate to="/panel" replace />;
  }

  // Check tenant admin requirement
  if (tenantAdminOnly && !isTenantAdmin()) {
    return <Navigate to="/panel" replace />;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/panel" replace />;
  }

  // Check all required permissions
  if (requiredPermissions && !hasAllPermissions(requiredPermissions)) {
    return <Navigate to="/panel" replace />;
  }

  // Check any required permission
  if (requireAnyPermission && !hasAnyPermission(requireAnyPermission)) {
    return <Navigate to="/panel" replace />;
  }

  // Check required module
  if (requiredModule && !hasModule(requiredModule)) {
    return <Navigate to="/panel" replace />;
  }

  return <>{children}</>;
}
